<template>
    <v-sheet elevation="4" width="100%" min-height="100" class="pa-3 mt-2">
        <template v-if="loading">
            <v-progress-circular
                indeterminate
                color="primary"
                size="64"
            ></v-progress-circular>
        </template>
        <template v-else>
            <v-autocomplete
                v-model="filters.item_id"
                :items="itemOptions"
                item-text="text"
                item-value="value"
                label="Filter by Item Name"
                clearable
            ></v-autocomplete>
            <div class="d-flex gap-3 mb-4 flex-wrap">
                <v-chip 
                    @click.stop="onOrdersClick(itemsStats.orderedIds)" class="ma-0" v-if="!hasOrderFilter">
                    <v-icon left class="mr-2">mdi-receipt</v-icon>
                    <strong>Orders:</strong>
                    <span class="ml-1">{{ itemsStats.orderCount }}</span>
                </v-chip>
                <v-chip 
                    @click.stop="onOrdersClick(null)" class="ma-0" color="primary" text-color="white" v-else>
                    <v-icon left class="mr-2">mdi-receipt</v-icon>
                    <strong>Orders:</strong>
                    <span class="ml-1">{{ itemsStats.orderCount }}</span>
                </v-chip>
          
              <template v-if="filters.item_id || filters.reference || filters.item_name || filters.order_id">
                <v-chip>
                  <v-icon left class="mr-2">mdi-package-variant-closed</v-icon>
                  <strong>Item total:</strong>
                  <span class="ml-1">{{ itemsStats.totalQty }} db -- ( {{ itemsStats.def_qty }} {{ itemsStats.unit }} )</span>
                </v-chip>
            
                <v-chip>
                  <v-icon left class="mr-2">mdi-warehouse</v-icon>
                  <strong>Stock:</strong>
                  <span class="ml-1">{{ itemsStats.totalStock }} db -- ( {{ itemsStats.def_qty }} {{ itemsStats.unit }} )</span>
                </v-chip>
            
                <v-chip>
                  <v-icon left class="mr-2">mdi-cash</v-icon>
                  <strong>Net total:</strong>
                  <span class="ml-1">{{ money(itemsStats.totalNet) }} {{ itemsStats.currency }}</span>
                </v-chip>
            
                <v-chip>
                  <v-icon left class="mr-2">mdi-cash-multiple</v-icon>
                  <strong>Gross total:</strong>
                  <span class="ml-1">{{ money(itemsStats.totalGross) }} {{ itemsStats.currency }}</span>
                </v-chip>
              </template>
            </div>
            
            <template v-if="filters.item_id || filters.reference || filters.item_name || filters.order_id">
                <v-card v-for="g in groupedItems" :key="g.key" class="mb-4">
                  <v-card-title>
                    {{ g.item_name }} <span v-if="g.item_code">({{ g.item_code }})</span>
                  </v-card-title>
              
                  <v-card-text>
                    <div><strong>Order ID-k:</strong> {{ g.orderIds.join(", ") }}</div>
                    <div><strong>Quantity összesen:</strong> {{ g.qty }} db -- {{ g.def_qty }} {{ g.unit }}</div>
                
                    <div v-if="g.unit_price_net"><strong>Unit net:</strong> {{ money(g.unit_price_net) }} {{ g.item_currency }}</div>
                    <div v-if="g.unit_price_gross"><strong>Unit gross:</strong> {{ money(g.unit_price_gross) }} {{ g.item_currency }}</div>
                    <div v-if="g.total_net"><strong>Total net:</strong> {{ money(g.total_net) }} {{ g.item_currency }}</div>
                    <div v-if="g.total_gross"><strong>Total gross:</strong> {{ money(g.total_gross) }} {{ g.item_currency }}</div>
                  </v-card-text>
                </v-card>
            </template>
        </template>
    </v-sheet>
</template>
<script>
import ApiService from "@/core/services/api.service";

export const ENDPOINT = "sales/Orders/";
export default {
  name: "OrderFilters",
  props: {
    hasOrderFilter: {
      type: Boolean,
      default: false
    },
    orderStatus:{
      type: String,
      default: 'Live',
    }
  },
  data() {
    return {
      loading: true,
      ordered_items: [],
      filters: {
        item_id: null,
        reference: "",
        item_name: "",
        order_id: "",
      },
      orderClicked: false,
    };
  },
  watch: {
    filters: {
        handler() {
            if(this.hasOrderFilter) {
                this.onOrdersClick(this.itemsStats.orderedIds);
            }
        },
        deep: true,
        }
    },
  computed: {
    itemOptions() {
        const map = new Map();

        for (const it of this.ordered_items) {
          if (!map.has(it.item_id)) {
            map.set(it.item_id, {
              value: it.item_id,
              item_name: it.item_name,
              item_code: it.reference,
              text: `${it.reference ? it.reference + " — " : ""}${it.item_name || ""}`.trim(),
            });
          }
        }

        return Array.from(map.values());
    },
    filteredItems() {
        let rows = [...this.ordered_items];

        if(this.filters.item_id) {
            rows = rows.filter(item => item.item_id === Number(this.filters.item_id));
        }
        if(this.filters.reference) {
            rows = rows.filter(item => item.item_code === this.filters.reference);
        }
        if(this.filters.item_name) {
            rows = rows.filter(item => item.item_name === this.filters.item_name);
        }
        if(this.filters.order_id) {
            rows = rows.filter(item => item.order_id === this.filters.order_id);
        }

      if (this.orderStatus === 'Live') {
          rows = rows.filter(item => {
              return item.order && item.order.marked_at === null;
          });
      } else if (this.orderStatus === 'Closed') {
          rows = rows.filter(item => {
              return item.order && item.order.marked_at !== null;
          });
      }
        
        return rows; // Return the filtered items
    },

    groupedItems() {
      const map = new Map();

      for (const r of this.filteredItems) {
        const key = String(r.item_id);

        if (!map.has(key)) {
          map.set(key, {
            key,
            item_id: r.item_id,
            item_code: r.item_code,
            item_name: r.item_name,
            item_currency: r.order?.currency || 'HUF',
            current_stock: r.current_stock,
            unit: r.unit,
            def_qty: r.default_qty,

            qty: 0,
            unit_price_net: 0,
            unit_price_gross: 0,
            total_net: 0,
            total_gross: 0,

            orderIds: new Set(),
            lines: [],
          });
        }

        const g = map.get(key);

        const qty = Number(r.quantity ?? r.qty ?? 0);
        g.qty += qty;


        const unitNet =
          r.unit_price_net != null ? Number(r.unit_price_net) :
          r.total_net != null && qty > 0 ? Number(r.total_net) / qty :
          0;
        
        const unitGross =
          r.unit_price_gross != null ? Number(r.unit_price_gross) :
          r.total_gross != null && qty > 0 ? Number(r.total_gross) / qty :
          0;

        const lineNet =
          r.total_net != null ? Number(r.total_net) :
          r.unit_price_net != null ? Number(r.unit_price_net) * qty :
          0;

        const lineGross =
          r.total_gross != null ? Number(r.total_gross) :
          r.unit_price_gross != null ? Number(r.unit_price_gross) * qty :
          0;

        g.unit_price_net = unitNet || 0;
        g.unit_price_gross = unitGross || 0;
        g.total_net += lineNet;
        g.total_gross += lineGross;

        if (r.order_id != null) g.orderIds.add(r.order_id);

        g.lines.push(r);
      }

      return Array.from(map.values()).map(g => ({
        ...g,
        orderIds: Array.from(g.orderIds),
      }));
    },

    itemsStats() {
      const uniqueItems = this.groupedItems.length;

      let totalQty = 0;
      let totalNet = 0;
      let totalGross = 0;
      let currency = null;
      let totalStock = 0;
      let unit = null;
      let def_qty = 0;

      const allOrders = new Set();

      for (const g of this.groupedItems) {
        totalQty += g.qty;
        totalNet += g.total_net;
        totalGross += g.total_gross;
        for (const oid of g.orderIds) allOrders.add(oid);
        currency = g.item_currency || 'HUF';
        totalStock = g.current_stock || 0;
        unit = g.unit || 'db';
        def_qty = g.def_qty || 0;
      }

      return {
        uniqueItems,
        totalQty,
        orderCount: allOrders.size,
        orderedIds: allOrders,
        totalNet,
        totalGross,
        currency,
        totalStock,
        unit,
        def_qty,
      };
    },
  },
  methods: {
    onOrdersClick(ids) {
        this.hasOrderFilter = !this.hasOrderFilter;
        const arr = Array.isArray(ids) ? ids : Array.from(ids || []);
        this.$emit("show-orders", arr);
    },
    fetchOrderItems() {
      ApiService.get(ENDPOINT + "getorderitems")
        .then((response) => {
          this.ordered_items = response.data.order_items;
        })
        .catch((e) => {
            console.log("STATUS:", e?.response?.status);
            console.log("DATA:", e?.response?.data);
            console.log("ERR:", e);
        })
        .finally(() => {
           this.loading = false
        });
    },
    money(v) {
        const n = Number(v || 0);
        return new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 0 }).format(n);
    },
  },
  mounted() {
    this.fetchOrderItems();
  },
}
</script>
<style>
.v-chip {
  border-radius: 0 !important;
}
</style>